import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterDatalakeSecuritizacoesInformeMensalTable = (props) => {
    const datalakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        datalakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        }).catch((err) => {
            props.loading(false)
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "CETIP": null,
            "ISIN": null,
            "DataReferencia": null,
            "PatrimonioSeparado": null,
            "PatrimonioSeparadoLiquido": null,
            "Disponibilidades": null,
            "Derivativos": null,
            "SaldoAdimplente": null,
            "SaldoInadimplente": null,
            "PrePagamento": null,
            "AgenciaRating": null,
            "Subordinacao": null,
            "OutrasInformacoes": null,
            "ContingenciasPatrimonio": null,
            "DataEntrega": null,
            "Status": null,
            "Versao": null,
            "ModalidadeEnvio": null,
            "Link": null
        }

        props.loading(true)
        return datalakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, CETIP, ISIN, DataReferencia, PatrimonioSeparado, 
            PatrimonioSeparadoLiquido, Disponibilidades, Derivativos, SaldoAdimplente, 
            SaldoInadimplente, PrePagamento, AgenciaRating, Subordinacao, OutrasInformacoes, 
            ContingenciasPatrimonio, DataEntrega, Status, Versao, ModalidadeEnvio, Link
        } = newRow

        const body = {
            "CETIP": CETIP,
            "ISIN": ISIN,
            "DataReferencia": DataReferencia,
            "PatrimonioSeparado": PatrimonioSeparado,
            "PatrimonioSeparadoLiquido": PatrimonioSeparadoLiquido,
            "Disponibilidades": Disponibilidades,
            "Derivativos": Derivativos,
            "SaldoAdimplente": SaldoAdimplente,
            "SaldoInadimplente": SaldoInadimplente,
            "PrePagamento": PrePagamento,
            "AgenciaRating": AgenciaRating,
            "Subordinacao": Subordinacao,
            "OutrasInformacoes": OutrasInformacoes,
            "ContingenciasPatrimonio": ContingenciasPatrimonio,
            "DataEntrega": DataEntrega,
            "Status": Status,
            "Versao": Versao,
            "ModalidadeEnvio": ModalidadeEnvio,
            "Link": Link
        }

        props.loading(true)
        return datalakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return datalakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }


    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        datalakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        datalakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'ISIN',
            headerName: 'ISIN',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'PatrimonioSeparado',
            headerName: 'PatrimonioSeparado',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'PatrimonioSeparadoLiquido',
            headerName: 'PatrimonioSeparadoLiquido',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'Disponibilidades',
            headerName: 'Disponibilidades',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'Derivativos',
            headerName: 'Derivativos',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'SaldoAdimplente',
            headerName: 'SaldoAdimplente',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'SaldoInadimplente',
            headerName: 'SaldoInadimplente',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'PrePagamento',
            headerName: 'PrePagamento',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'AgenciaRating',
            headerName: 'AgenciaRating',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Subordinacao',
            headerName: 'Subordinacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'OutrasInformacoes',
            headerName: 'OutrasInformacoes',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'ContingenciasPatrimonio',
            headerName: 'ContingenciasPatrimonio',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'DataEntrega',
            headerName: 'DataEntrega',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'Status',
            headerName: 'Status',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Versao',
            headerName: 'Versao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'ModalidadeEnvio',
            headerName: 'ModalidadeEnvio',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Link',
            headerName: 'Link',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                pagination={pagination}
                onChangePage={changePage}
                filterFunction={onFilter}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeSecuritizacoesInformeMensalTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterDatalakeSecuritizacoesInformeMensalTable